import React, { useState, useEffect, useRef } from 'react';
import './styles/ExpandableText.css';

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
}

const ExpandableText: React.FC<ExpandableTextProps> = ({ 
  text, 
  maxLines = 2,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTruncated(element.scrollHeight > element.clientHeight + 1);
      }
    };

    const timeout = setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);
    
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text, maxLines]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`expandable-text ${className}`}>
      <p 
        ref={textRef}
        className={`expandable-text__content ${isExpanded ? 'expandable-text__content--expanded' : ''}`}
        style={{ WebkitLineClamp: isExpanded ? 'unset' : maxLines }}
      >
        {text}
      </p>
      {isTruncated && (
        <button 
          className="expandable-text__toggle"
          onClick={handleToggle}
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Show less' : 'Show more'}
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d={isExpanded ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} 
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ExpandableText;